import React, { useState, useEffect, useRef } from 'react';
import { FaTimes } from 'react-icons/fa';
import UsersIcon from '../../assets/Users.svg';
import './InviteMembersModal.css';
import { useI18n } from '../../i18n';

const InviteMembersModal = ({
    isOpen,
    onClose,
    team,
    availableUsers,
    loadingUsers,
    onInvite,
    onRoleChange,
    onRemoveMember,
    inviting,
}) => {
    const { t } = useI18n();
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [inviteRole, setInviteRole] = useState('member');
    const [searchTerm, setSearchTerm] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Filter available users based on search
    const filteredUsers = availableUsers.filter(user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Get selected user names for display
    const selectedUsers = availableUsers.filter(u => selectedUserIds.includes(u.id));
    const getDisplayValue = () => {
        if (selectedUsers.length === 0) return '';
        if (selectedUsers.length <= 2) {
            return selectedUsers.map(u => u.name).join(', ');
        }
        const firstTwo = selectedUsers.slice(0, 2).map(u => u.name).join(', ');
        return t('pages.teams.see_more', { count: selectedUsers.length - 2 });
    };
    const displayValue = getDisplayValue();

    // Toggle user selection
    const handleToggleUser = (userId) => {
        if (selectedUserIds.includes(userId)) {
            setSelectedUserIds(selectedUserIds.filter(id => id !== userId));
        } else {
            setSelectedUserIds([...selectedUserIds, userId]);
        }
    };

    // Handle invite button click
    const handleInvite = () => {
        if (selectedUserIds.length > 0 && onInvite) {
            onInvite(selectedUserIds, inviteRole);
            setSelectedUserIds([]);
            setInviteRole('member');
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setSelectedUserIds([]);
            setInviteRole('member');
            setSearchTerm('');
            setIsDropdownOpen(false);
        }
    }, [isOpen]);

    if (!isOpen || !team) return null;

    const currentMembers = team.members || [];

    // Generate avatar color based on name
    const getAvatarColor = (name) => {
        const colors = [
            '#6366F1', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981',
            '#3B82F6', '#EF4444', '#14B8A6', '#F97316', '#6B7280'
        ];
        const index = name ? name.charCodeAt(0) % colors.length : 0;
        return colors[index];
    };

    return (
        <div className="invite-members-modal-overlay" onClick={onClose}>
            <div className="invite-members-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="team-modal-header">
                    <h2 className="team-modal-title">{t('pages.teams.current_members')}</h2>
                    <button
                        className="team-modal-close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Body */}
                <div className="invite-members-modal-body">
                    {/* Invite Section */}
                    <div className="invite-section">
                        <label className="invite-section-label">{t('pages.teams.invite_members')}</label>
                        <div className="invite-input-row">
                            <div className="invite-select-container" ref={dropdownRef}>
                                <div
                                    className={`invite-select-field ${isDropdownOpen ? 'open' : ''}`}
                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                >
                                    <div className="invite-select-value">
                                        {selectedUsers.length > 0 ? (
                                            <span className="selected-text">{displayValue}</span>
                                        ) : (
                                            <span className="placeholder-text">{t('common.actions.search')}</span>
                                        )}
                                    </div>
                                    <div className="invite-select-arrow">
                                        <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                                            <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                </div>

                                {isDropdownOpen && (
                                    <div className="invite-select-dropdown">
                                        <div className="invite-dropdown-search">
                                            <input
                                                type="text"
                                                placeholder={t('common.actions.search')}
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                                autoFocus
                                            />
                                        </div>
                                        <div className="invite-dropdown-list">
                                            {loadingUsers ? (
                                                <div className="invite-dropdown-empty">{t('common.status.loading')}</div>
                                            ) : filteredUsers.length === 0 ? (
                                                <div className="invite-dropdown-empty">
                                                    {searchTerm ? t('common.empty_states.no_results') : t('pages.teams.no_users_available_invite')}
                                                </div>
                                            ) : (
                                                filteredUsers.map(user => (
                                                    <div
                                                        key={user.id}
                                                        className={`invite-dropdown-item ${selectedUserIds.includes(user.id) ? 'selected' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleToggleUser(user.id);
                                                        }}
                                                    >
                                                        <div className="invite-dropdown-checkbox">
                                                            {selectedUserIds.includes(user.id) && (
                                                                <svg viewBox="0 0 20 20" fill="currentColor">
                                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                </svg>
                                                            )}
                                                        </div>
                                                        <span className="invite-dropdown-name">{user.name}</span>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Role dropdown - only shown when users are selected */}
                            {selectedUsers.length > 0 && (
                                <select
                                    className="invite-role-select"
                                    value={inviteRole}
                                    onChange={(e) => setInviteRole(e.target.value)}
                                >
                                    <option value="member">{t('pages.teams.labels.member')}</option>
                                    <option value="owner">{t('pages.teams.labels.owner')}</option>
                                </select>
                            )}

                            <button
                                className={`invite-btn ${selectedUsers.length > 0 ? 'active' : ''}`}
                                onClick={handleInvite}
                                disabled={selectedUsers.length === 0 || inviting}
                            >
                                {inviting ? t('common.status.sending') : t('common.actions.invite')}
                            </button>
                        </div>
                    </div>

                    {/* Current Members Section */}
                    <div className="current-members-section">
                        <div className="current-members-header">
                            <img src={UsersIcon} alt="" className="current-members-icon" />
                            <span>{t('pages.teams.labels.members')}</span>
                        </div>

                        <div className="current-members-list">
                            {currentMembers.map((member) => (
                                <div key={member.id} className="member-row">
                                    <div className="member-info">
                                        <div className='member-info-container'>
                                            <div
                                                className="member-avatar"
                                                style={{ backgroundColor: getAvatarColor(member.name) }}
                                            >
                                                {member.name?.charAt(0)?.toUpperCase() || 'U'}
                                            </div>
                                            <div className="member-details">
                                                <div className="member-name">{member.name}</div>
                                                <div className="member-email">{member.email}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="member-role">
                                        {member.role === 'owner' ? (
                                            <span className="role-owner">{t('pages.teams.labels.owner')}</span>
                                        ) : (
                                            <select
                                                className="role-select"
                                                value={member.role || 'member'}
                                                onChange={(e) => onRoleChange && onRoleChange(member.id, e.target.value)}
                                            >
                                                <option value="member">{t('pages.teams.labels.member')}</option>
                                                <option value="owner">{t('pages.teams.labels.owner')}</option>
                                            </select>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                {/* Footer */}
                <div className="team-modal-footer">
                    <button
                        type="button"
                        className="team-modal-btn team-btn-secondary"
                        onClick={onClose}
                    >
                        {t('common.actions.cancel')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InviteMembersModal;
